import { useQuery } from '@tanstack/react-query';
import { FC, useRef, useState } from 'react';
import styled from 'styled-components';

import FilterButton from '@/components/FilterDropdown/FilterButton';
import { Label } from '@/components/Forms';
import Text from '@/components/Text';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { ChannelResource } from '@/types/resources';
import { api } from '@/utils/api';
import { scrollBarStyles } from '@/utils/theme';

const Relative = styled.div`
  position: relative;
`;

const Box = styled.div`
  z-index: 3;
  position: absolute;
  right: 0;
  top: calc(100% + 12px);
  width: 187px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
`;

const BoxHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
`;

const ChannelsList = styled.div`
  padding: 8px 4px;
  max-height: 374px;
  overflow-y: auto;
  ${scrollBarStyles};
`;

const ChannelButton = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  transition: background ease-out 0.2s;
  &:hover {
    background: ${({ theme }) => theme.colors.grayscaleLight};
  }
`;

const StyledLabel = styled(Label)`
  min-height: 40px;
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin: 0;
  font-size: 11px;
  width: 100%;
  padding: 4px 12px;
  padding-right: 37px;
`;

const Checkmark = styled.span`
  width: 16px;
  height: 16px;
  top: 0;
  bottom: 0;
  right: 12px;
  margin: auto 0;
  position: absolute;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  border-radius: 1px;
  transition: 0.2s ease-out all;
  &::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 5px;
    height: 9px;
    border: solid ${({ theme }) => theme.colors.white};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: none;
  }
`;

const Input = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 1px;
  width: 1px;
  &:checked ~ ${Checkmark} {
    background: ${({ theme }) => theme.colors.purple};
    border-color: ${({ theme }) => theme.colors.purple};
    &::after {
      display: block;
    }
  }
`;

const ResetButton = styled(Text)`
  transition: color ease-out 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

interface Props {
  selectedChannels: number[];
  setSelectedChannels: React.Dispatch<React.SetStateAction<number[]>>;
}

const EditorialDistributionChannelsFilter: FC<Props> = ({
  selectedChannels,
  setSelectedChannels,
}) => {
  const [showFilter, setShowFilter] = useState(false);

  const { data: channels } = useQuery<ChannelResource[]>(
    ['editorial_channels'],
    () => api.get<never, ChannelResource[]>('/v1/editorial/channels'),
    {
      refetchOnMount: false,
      staleTime: 1000 * 120,
    },
  );

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setShowFilter(false));

  const onChange = (id: number) => {
    setSelectedChannels((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  return (
    <Relative ref={ref}>
      <FilterButton
        onClick={() => setShowFilter((prev) => !prev)}
        isActive={showFilter}
        filterApplied={!!selectedChannels.length}
        text="Page"
        type="initial"
      />
      {showFilter && (
        <Box>
          <BoxHeader>
            <Text $type="smallBold">By Page:</Text>
            <ResetButton
              $color="gray"
              $type="extraSmallSemiBold"
              as="button"
              onClick={() => setSelectedChannels([])}
            >
              Reset
            </ResetButton>
          </BoxHeader>
          {!!channels?.length && (
            <ChannelsList>
              {channels.map((channel) => (
                <ChannelButton key={channel.id}>
                  <StyledLabel>
                    {channel.name}
                    <Input
                      type="checkbox"
                      checked={selectedChannels.includes(channel.id)}
                      onChange={() => onChange(channel.id)}
                    />
                    <Checkmark />
                  </StyledLabel>
                </ChannelButton>
              ))}
            </ChannelsList>
          )}
        </Box>
      )}
    </Relative>
  );
};

export default EditorialDistributionChannelsFilter;
